import { useEffect, useState } from "react";
import CommonCheckBox from "../../../../common/components/checkbox/CommonCheckBox";
import styles from "./AcceptOffer.module.scss";
import { ArrowLeft } from "../../../../assets/icons";
import { useDeclineBnplOfferMutation, useLazyGetBnplOfferDetailsQuery } from "../../../../service/order-service";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { hideLoader, showLoader } from "../../../../stores/slices/loader-slice";
import ConfirmationDialog from "../../../../common/components/dialog/ConfirmationDialog";
import { formatDate, getOrdinalPosition } from "../../../../utils";
import { useView } from "../provider/ViewContext";
import { Link } from "react-router-dom";
import { ApplicationRoutes } from "../../../../routes";

export interface AcceptOfferProps {
	BnplId: string;
	handleBack: () => void;
}

const AcceptOffer: React.FC<AcceptOfferProps> = ({ BnplId, handleBack }) => {
	const dispatch = useDispatch();
	const { setView } = useView();
	const [getOfferDetails, { data }] = useLazyGetBnplOfferDetailsQuery();
	const [declineOffer, { isLoading }] = useDeclineBnplOfferMutation();
	const [showConfirm, setShowConfirm] = useState(false);
	const [isAcceptTerms, setIsAcceptTerms] = useState(false);

	useEffect(() => {
		handleGetOfferDetails();
	}, []);

	const handleGetOfferDetails = async () => {
		dispatch(showLoader());
		await getOfferDetails(BnplId)
			.unwrap()
			.then()
			.catch(err => toast.error(err?.data?.title as string))
			.finally(() => {
				dispatch(hideLoader());
			});
	};

	const handleDeclineOffer = async () => {
		await declineOffer(BnplId)
			.unwrap()
			.then(() => {
				handleBack();
				toast.success("Offer declined successfully");
			})
			.catch(err => toast.error(err?.data?.title as string))
			.finally(() => {
				toggleDialog();
			});
	};

	const toggleDialog = () => {
		setShowConfirm(!showConfirm);
	};

	return (
		<>
			{showConfirm && (
				<ConfirmationDialog
					title="Are you sure you want to decline this offer?"
					description="Kindly confirm this action"
					actionLoading={isLoading}
					onConfirm={handleDeclineOffer}
					onCloseDialog={toggleDialog}
					btnText="Decline"
				/>
			)}
			<div className={`${styles.headerSection} ${styles.standardSectionPadding}`}>
				<button style={{ cursor: "pointer" }} onClick={handleBack}>
					<ArrowLeft />
				</button>
				<div className={styles.text}></div>
			</div>
			<div className={styles.container}>
				<div>
					<div className={styles.title}>Your BNPL Request has been approved, see breakdown below:</div>
					<div>
						<div className={styles.centerSection}>
							<div className={styles.centerSectionTitle}>PAYMENT information</div>
							<table className={styles.offerTable}>
								<thead>
									<tr>
										<th className={styles.property}>Total Payment</th>
										<th className={styles.value}>₦{data?.totalPayment?.toLocaleString()}</th>
									</tr>
								</thead>
								<tbody>
									<tr>
										<td className={styles.property}>30% Initial Deposit</td>
										<td className={styles.value}>
											<span className={styles.date}>Deposit Now</span> <span className={styles.divider}></span>₦{data?.deposit?.toLocaleString()}
										</td>
									</tr>
									{data?.payments?.map((payment, index) => {
										return (
											<tr key={index}>
												<td className={styles.property}>{getOrdinalPosition(index + 1)} Installment</td>
												<td className={styles.value}>
													<span className={styles.date}>{formatDate(payment?.paymentDate)}</span> <span className={styles.divider}></span>{" "}
													<span className={styles.amount}>₦{payment?.amount?.toLocaleString()}</span>
												</td>
												{/* <td ><span>{formatDate(payment?.paymentDate)}</span> <span className={styles.divider}></span> <span className={styles.amount}>₦{payment?.amount?.toLocaleString()}</span></td> */}
											</tr>
										);
									})}
								</tbody>
							</table>

							<div className={styles.termsRow}>
								<CommonCheckBox onToggle={() => setIsAcceptTerms(!isAcceptTerms)} className={styles.offerCheckbox} />{" "}
								<span>
									I have read and accept the&nbsp;
									<Link to={ApplicationRoutes.TERMS} className={`${styles.sectionItem} ${styles.noBorder}`}>
										<span className={styles.emphasis}>Terms</span> and <span className={styles.emphasis}>Conditions</span>
									</Link>
										
								</span>
							</div>
						</div>
						<div className={styles.buttonSection}>
							<button disabled={isLoading} onClick={toggleDialog} className={styles.leftBtn}>
								<svg width="25" height="24" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg">
									<mask id="mask0_1116_10130" maskUnits="userSpaceOnUse" x="0" y="0" width="25" height="24">
										<rect width="24" height="24" transform="matrix(-1 0 0 1 24.5 0)" fill="#D9D9D9" />
									</mask>
									<g mask="url(#mask0_1116_10130)">
										<path
											d="M11.5538 12L15.6269 7.92689C15.7654 7.78844 15.8362 7.6144 15.8394 7.40479C15.8426 7.19519 15.7718 7.01795 15.6269 6.87309C15.482 6.7282 15.3064 6.65576 15.1 6.65576C14.8936 6.65576 14.718 6.7282 14.5731 6.87309L10.0789 11.3673C9.98529 11.4609 9.91926 11.5596 9.88081 11.6635C9.84235 11.7673 9.82311 11.8795 9.82311 12C9.82311 12.1205 9.84235 12.2327 9.88081 12.3365C9.91926 12.4404 9.98529 12.5391 10.0789 12.6327L14.5731 17.1269C14.7116 17.2654 14.8856 17.3362 15.0952 17.3394C15.3048 17.3426 15.482 17.2718 15.6269 17.1269C15.7718 16.982 15.8442 16.8064 15.8442 16.6C15.8442 16.3936 15.7718 16.218 15.6269 16.0731L11.5538 12Z"
											fill="#DE841B"
										/>
									</g>
								</svg>
								Reject Offer
							</button>
							<button
								className={styles.rightBtn}
								onClick={() => {
									if (!isAcceptTerms) {
										toast.warning("You need to accept terms");
										return;
									}
									setView("authorizePayment");
								}}
							>
								Accept Offer
								<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
									<mask id="mask0_1116_10138" maskUnits="userSpaceOnUse" x="0" y="0" width="24" height="24">
										<rect width="24" height="24" fill="#D9D9D9" />
									</mask>
									<g mask="url(#mask0_1116_10138)">
										<path
											d="M12.9462 12L8.87309 7.92689C8.73462 7.78844 8.66379 7.6144 8.66059 7.40479C8.65737 7.19519 8.7282 7.01795 8.87309 6.87309C9.01795 6.7282 9.19359 6.65576 9.39999 6.65576C9.60639 6.65576 9.78202 6.7282 9.92689 6.87309L14.4211 11.3673C14.5147 11.4609 14.5807 11.5596 14.6192 11.6635C14.6577 11.7673 14.6769 11.8795 14.6769 12C14.6769 12.1205 14.6577 12.2327 14.6192 12.3365C14.5807 12.4404 14.5147 12.5391 14.4211 12.6327L9.92689 17.1269C9.78844 17.2654 9.6144 17.3362 9.40479 17.3394C9.19519 17.3426 9.01795 17.2718 8.87309 17.1269C8.7282 16.982 8.65576 16.8064 8.65576 16.6C8.65576 16.3936 8.7282 16.218 8.87309 16.0731L12.9462 12Z"
											fill="white"
										/>
									</g>
								</svg>
							</button>
						</div>
					</div>
				</div>
			</div>
		</>
	);
};
export default AcceptOffer;
